/**
 * bookCache.ts
 * IndexedDB 기반 교재 데이터 캐시 서비스
 *
 * 캐시 무효화 전략:
 * - 교재의 bookChapterSets 컬렉션 중 가장 최근 updatedAt을 캐시 버전 키로 사용
 * - 이 값은 모든 저장 함수가 updatedAt: Date.now()를 쓰기 때문에 자동으로 갱신됨
 * - 추가 코드 수정 없이 캐시 무효화가 보장됨
 */

const DB_NAME = 'qgenBookCache';
const DB_VERSION = 1;
const STORE_BOOK_SETS = 'bookSets';       // 단원 목록 (지문/단어/문제 제외)
const STORE_SET_CONTENT = 'setContent';   // 단원 내용 (지문+단어+문제)
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7일

// ─────────────────────────────────────────────
// IndexedDB 초기화
// ─────────────────────────────────────────────

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
    if (dbPromise) return dbPromise;

    dbPromise = new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB not supported'));
            return;
        }

        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;

            if (!db.objectStoreNames.contains(STORE_BOOK_SETS)) {
                const store = db.createObjectStore(STORE_BOOK_SETS, { keyPath: 'cacheKey' });
                store.createIndex('bookId', 'bookId', { unique: false });
                store.createIndex('fetchedAt', 'fetchedAt', { unique: false });
            }

            if (!db.objectStoreNames.contains(STORE_SET_CONTENT)) {
                const store = db.createObjectStore(STORE_SET_CONTENT, { keyPath: 'cacheKey' });
                store.createIndex('bookId', 'bookId', { unique: false });
                store.createIndex('fetchedAt', 'fetchedAt', { unique: false });
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => {
            dbPromise = null;
            reject(request.error);
        };
    });

    return dbPromise;
}

// ─────────────────────────────────────────────
// 내부 헬퍼
// ─────────────────────────────────────────────

function idbGet<T>(store: string, key: string): Promise<T | null> {
    return openDB().then(db => new Promise<T | null>((resolve) => {
        const tx = db.transaction(store, 'readonly');
        const req = tx.objectStore(store).get(key);
        req.onsuccess = () => resolve((req.result as T) ?? null);
        req.onerror = () => resolve(null);
    })).catch(() => null);
}

function idbPut(store: string, value: object): Promise<void> {
    return openDB().then(db => new Promise<void>((resolve) => {
        const tx = db.transaction(store, 'readwrite');
        const req = tx.objectStore(store).put(value);
        req.onsuccess = () => resolve();
        req.onerror = () => resolve(); // 저장 실패는 무시 (캐시이므로)
    })).catch(() => { /* silent fail */ });
}

function idbDeleteByIndex(store: string, indexName: string, value: string): Promise<void> {
    return openDB().then(db => new Promise<void>((resolve) => {
        const tx = db.transaction(store, 'readwrite');
        const objectStore = tx.objectStore(store);
        const index = objectStore.index(indexName);
        const req = index.openCursor(IDBKeyRange.only(value));
        req.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
            if (cursor) {
                cursor.delete();
                cursor.continue();
            } else {
                resolve();
            }
        };
        req.onerror = () => resolve();
    })).catch(() => { /* silent fail */ });
}

// ─────────────────────────────────────────────
// 단원 목록 캐시 (bookSets)
// ─────────────────────────────────────────────

export interface BookSetsCache {
    cacheKey: string;       // `${bookId}_${cacheVersion}`
    bookId: string;
    cacheVersion: number;   // bookChapterSets 최신 updatedAt
    sets: any[];            // EnglishSet[] (paragraphs/units/vocabularies 제외)
    fetchedAt: number;
}

/**
 * 단원 목록 캐시 조회
 * @param bookId 교재 ID
 * @param cacheVersion bookChapterSets 최신 updatedAt
 * @returns 캐시 히트 시 단원 배열, 미스 시 null
 */
export async function getBookSetsCache(bookId: string, cacheVersion: number): Promise<any[] | null> {
    if (!cacheVersion) return null;
    try {
        const cacheKey = `${bookId}_${cacheVersion}`;
        const cached = await idbGet<BookSetsCache>(STORE_BOOK_SETS, cacheKey);
        if (!cached) return null;

        // TTL 체크
        if (Date.now() - cached.fetchedAt > CACHE_TTL_MS) {
            return null;
        }

        return cached.sets;
    } catch {
        return null;
    }
}

/**
 * 단원 목록 캐시 저장
 */
export async function setBookSetsCache(bookId: string, cacheVersion: number, sets: any[]): Promise<void> {
    if (!cacheVersion) return;
    try {
        const cacheKey = `${bookId}_${cacheVersion}`;
        // 무거운 content 필드 제외 후 저장
        const lightSets = sets.map(s => ({
            ...s,
            paragraphs: undefined,
            units: undefined,
            vocabularies: undefined,
        }));
        await idbPut(STORE_BOOK_SETS, {
            cacheKey,
            bookId,
            cacheVersion,
            sets: lightSets,
            fetchedAt: Date.now(),
        });
    } catch { /* silent fail */ }
}

// ─────────────────────────────────────────────
// 단원 내용 캐시 (setContent)
// ─────────────────────────────────────────────

export interface SetContentData {
    paragraphs: any[];
    units: any[];
    vocabularies: any[];
}

export interface SetContentCache {
    cacheKey: string;       // `${setId}_${cacheVersion}`
    setId: string;
    bookId: string;
    cacheVersion: number;
    content: SetContentData;
    fetchedAt: number;
}

/**
 * 단원 내용(지문+단어+문제) 캐시 조회
 */
export async function getSetContentCache(setId: string, cacheVersion: number): Promise<SetContentData | null> {
    if (!cacheVersion) return null;
    try {
        const cacheKey = `${setId}_${cacheVersion}`;
        const cached = await idbGet<SetContentCache>(STORE_SET_CONTENT, cacheKey);
        if (!cached) return null;

        if (Date.now() - cached.fetchedAt > CACHE_TTL_MS) {
            return null;
        }

        return cached.content;
    } catch {
        return null;
    }
}

/**
 * 단원 내용 캐시 저장
 */
export async function setSetContentCache(
    setId: string,
    bookId: string,
    cacheVersion: number,
    content: SetContentData
): Promise<void> {
    if (!cacheVersion) return;
    try {
        const cacheKey = `${setId}_${cacheVersion}`;
        await idbPut(STORE_SET_CONTENT, {
            cacheKey,
            setId,
            bookId,
            cacheVersion,
            content,
            fetchedAt: Date.now(),
        });
    } catch { /* silent fail */ }
}

// ─────────────────────────────────────────────
// 캐시 무효화
// ─────────────────────────────────────────────

/**
 * 특정 교재의 모든 캐시 삭제 (선생님이 수정한 경우 명시적 무효화용)
 * - 일반적으로는 cacheVersion(updatedAt) 키 불일치로 자동 무효화되므로 필수는 아님
 */
export async function invalidateBookCache(bookId: string): Promise<void> {
    try {
        await Promise.all([
            idbDeleteByIndex(STORE_BOOK_SETS, 'bookId', bookId),
            idbDeleteByIndex(STORE_SET_CONTENT, 'bookId', bookId),
        ]);
    } catch { /* silent fail */ }
}

// ─────────────────────────────────────────────
// 만료 캐시 자동 정리
// ─────────────────────────────────────────────

/**
 * 7일 이상 된 캐시 항목 자동 삭제
 * - index.tsx 앱 시작 시 1회 비동기 호출
 */
export async function cleanupOldCache(): Promise<void> {
    const cutoff = Date.now() - CACHE_TTL_MS;

    const cleanup = (store: string) => openDB().then(db => new Promise<void>((resolve) => {
        const tx = db.transaction(store, 'readwrite');
        const objectStore = tx.objectStore(store);
        const index = objectStore.index('fetchedAt');
        const req = index.openCursor(IDBKeyRange.upperBound(cutoff));
        req.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
            if (cursor) {
                cursor.delete();
                cursor.continue();
            } else {
                resolve();
            }
        };
        req.onerror = () => resolve();
    })).catch(() => { /* silent fail */ });

    await Promise.all([
        cleanup(STORE_BOOK_SETS),
        cleanup(STORE_SET_CONTENT),
    ]);
}
